
import React, { useState, useEffect } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { 
  createRoundRobinGroup, 
  updateRoundRobinGroup, 
  getAvailableColumns,
  getAvailableUsers,
  type RoundRobinGroup,
  type AvailableColumn,
  type AvailableUser
} from '@/actions/round-robin-groups';

interface RoundRobinGroupModalProps {
  group?: RoundRobinGroup | null;
  onSuccess: () => void;
  onClose: () => void;
}

const RoundRobinGroupModal: React.FC<RoundRobinGroupModalProps> = ({
  group,
  onSuccess,
  onClose
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableColumns, setAvailableColumns] = useState<AvailableColumn[]>([]);
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    group_name: '',
    column_id: 0,
    option_ids: [] as number[],
    user_ids: [] as number[],
  });

  const [selectedColumn, setSelectedColumn] = useState<AvailableColumn | null>(null);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setIsLoadingData(true);
      try {
        const [columnsResponse, usersResponse] = await Promise.all([
          getAvailableColumns(),
          getAvailableUsers()
        ]);

        if (columnsResponse.success && columnsResponse.data) {
          setAvailableColumns(columnsResponse.data);
        }

        if (usersResponse.success && usersResponse.data) {
          setAvailableUsers(usersResponse.data);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, []);

  // Initialize form with existing group data
  useEffect(() => {
    if (group && availableColumns.length > 0) {
      setFormData({
        group_name: group.group_name,
        column_id: group.column_id,
        option_ids: group.groupOptions?.map(opt => opt.option.opt_id) || [],
        user_ids: group.groupUsers?.map(user => user.user.userId) || [],
      });

      // Find and set selected column
      const column = availableColumns.find(col => col.col_id === group.column_id);
      if (column) {
        setSelectedColumn(column);
      }
    }
  }, [group, availableColumns]);

  const handleColumnChange = (columnId: number) => {
    const column = availableColumns.find(col => col.col_id === columnId);
    setSelectedColumn(column || null);
    setFormData(prev => ({
      ...prev,
      column_id: columnId,
      option_ids: [] // Reset selected options when column changes
    }));
  };

  const handleOptionToggle = (optionId: number) => {
    setFormData(prev => ({
      ...prev,
      option_ids: prev.option_ids.includes(optionId)
        ? prev.option_ids.filter(id => id !== optionId)
        : [...prev.option_ids, optionId]
    }));
  };

  const handleUserToggle = (userId: number) => {
    setFormData(prev => ({
      ...prev,
      user_ids: prev.user_ids.includes(userId)
        ? prev.user_ids.filter(id => id !== userId)
        : [...prev.user_ids, userId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.group_name.trim()) {
      alert('Please enter a group name');
      return;
    }

    if (!formData.column_id) {
      alert('Please select a field');
      return;
    }

    if (formData.option_ids.length === 0) {
      alert('Please select at least one option');
      return;
    }

    if (formData.user_ids.length === 0) {
      alert('Please select at least one user');
      return;
    }

    setIsSubmitting(true);

    try {
      let response;
      if (group) {
        response = await updateRoundRobinGroup(group.group_id, formData);
      } else {
        response = await createRoundRobinGroup(formData);
      }

      if (response.success) {
        onSuccess();
      } else {
        alert(response.error || 'Failed to save round robin group');
      }
    } catch (error) {
      console.error('Error saving round robin group:', error);
      alert('An error occurred while saving the round robin group');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className="flex flex-col h-full min-h-[500px]">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-brand-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading form data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-[600px]">
      {/* Form Content */}
      <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Group Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Group Name *
            </label>
            <input
              type="text"
              value={formData.group_name}
              onChange={(e) => setFormData(prev => ({ ...prev, group_name: e.target.value }))}
              placeholder="e.g., Facebook Leads Group"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              disabled={isSubmitting}
            />
          </div>

          {/* Field Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Based On Field *
            </label>
            <select
              value={formData.column_id}
              onChange={(e) => handleColumnChange(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              disabled={isSubmitting}
            >
              <option value={0}>Select a field...</option>
              {availableColumns.map((column) => (
                <option key={column.col_id} value={column.col_id}>
                  {column.col_label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Choose the field that will trigger this round robin group
            </p>
          </div>

          {/* Options Selection */}
          {selectedColumn && selectedColumn.options && selectedColumn.options.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Target Options * 
                <span className="text-xs font-normal text-gray-500 ml-1">
                  (Select which values will trigger this group)
                </span>
              </label>
              <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                {selectedColumn.options.map((option) => (
                  <label
                    key={option.opt_id}
                    className="flex items-center space-x-2 hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      checked={formData.option_ids.includes(option.opt_id)}
                      onChange={() => handleOptionToggle(option.opt_id)}
                      disabled={isSubmitting}
                    />
                    <span className="text-sm text-gray-900 dark:text-white select-none">
                      {option.opt_name}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Users Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Assign Users *
              <span className="text-xs font-normal text-gray-500 ml-1">
                (Users will receive leads in round robin order)
              </span>
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-3">
              {availableUsers.length > 0 ? (
                availableUsers.map((user) => (
                  <label
                    key={user.userId}
                    className="flex items-center space-x-2 hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      checked={formData.user_ids.includes(user.userId)}
                      onChange={() => handleUserToggle(user.userId)}
                      disabled={isSubmitting}
                    />
                    <span className="text-sm text-gray-900 dark:text-white select-none">
                      {user.firstName} {user.lastName}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      ({user.email})
                    </span>
                  </label>
                ))
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                  No users available
                </p>
              )}
            </div>
          </div>

        </div>

        {/* Fixed Footer Actions */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-800 flex-shrink-0">
          <div className="flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-brandColor border border-transparent rounded-lg hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {group ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {group ? 'Update Group' : 'Create Group'}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RoundRobinGroupModal;